import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import {
  vdjColor, vdjLoop, vdjBeatBreath, vdjBarChroma, VdjPost, VdjComfort, VDJ_PRESETS
} from './lib/vdjLook.js';

// Outrun - the classic synthwave VJ loop: an endless neon grid running
// toward a striped setting sun, between two wireframe mountain ranges, under
// a starry gradient sky. Synthwave, nu-disco, chillwave, electronic pop,
// French touch. Applies the LOOK VDJ LOUNGE (lib/vdjLook.js, preset
// 'lounge'): cuts on one phrase in two only, one-bar fades allowed, long
// loops, the beat a +8 % breath, the RGB split on the bar, no flash.
//
// THE GRID IS DRAWN, NOT MODELLED. One plane of 200 x 120 quads (24k
// vertices), and the lines are computed per pixel from the plane's own grid
// coordinate: distance to the nearest line divided by fwidth = distance in
// PIXELS, as in Infinity. A line has a world half-width (LINE_W) that sets
// its width in pixels at its depth, capped at CORE_MAX near the camera;
// below one pixel it keeps one pixel and dims in proportion, and once the
// cells themselves shrink to a few pixels the line fades into its own mean
// coverage. No crawling, no moire, a stable glow at the horizon.
//
// THE SCROLL NEVER SWIMS. The plane moves toward the camera by less than one
// row (uOff) and jumps back by exactly one row, while the mountains' noise is
// sampled at the row's LATTICE position (position.z - uSnap): every vertex
// samples the same world points it would if the mesh really travelled, so
// the ridges hold still on the ground. The scroll is the integral of the
// beat clock (one case per beat, x 1.5 after a drop): a rate, never a
// distance, so a tempo change or the drop never makes the road jump.
// It wraps modulo SCROLL_PERIOD, a multiple of every lattice in sight (rows,
// dashes, noise octaves), so the wrap is invisible.
//
// THE SUN sits in the sky quad, half set on the horizon, its centre a little
// above the cover's, 35 % of the width across (capped so it fits the
// height): warm yellow on top to magenta at the horizon, stripes cut into
// the lower half of the visible disc that thicken as they sink, one stripe
// per four beats. The cover is read into it in grey, low contrast, from a
// blurred mip, mixed at 20 %. Only the top of the disc crosses the bloom
// threshold; the halo stays under it.
//
// The horizon IS the sun's centre line, so the camera pitch is solved from
// it: looking down by atan(yh tan(fov/2)) puts the plane's vanishing line at
// screen height yh.

const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'outrun',
  family: 'geometric',
  danceability: 0.68,
  requiresTier: 0,
  energyLevel: 0.60,
  bpmMin: 80,
  bpmMax: 125,
  movement: 0.55,
  tonality: 0.75,
  enabled: true
};

const LOUNGE = VDJ_PRESETS.lounge;

const TIER = {
  0: { nx: 120, nz: 72, clouds: false },
  1: { nx: 200, nz: 120, clouds: true },
  2: { nx: 200, nz: 120, clouds: true }
};
const DEFAULT_TIER = 2;

// --- the world, in grid cases ---------------------------------------------------
const DEPTH = 72;                 // the plane's far edge
const X_CORE = 36;                // uniform columns out to here, then geometric
const X_MAX = 320;                // wide enough to reach the frame's sides at the far edge
const SCROLL_PERIOD = 240;        // multiple of the rows (0.6 / 1.0), the dashes (2) and the noise lattices
const CAM_H = 2.6;
const FOV = 50;
const MTN_H = 6.0;                // mountain height at bands.mid 0.5
const LINE_W = 0.022;             // world half-width of a grid line
const CORE_MAX = 1.25;            // px: half-width cap near the camera (2.5 px lines)
const HALO_PX = 3.0;
const HALO_GAIN = 0.32;
const ROAD_HALF = 1.5;            // three cases
const BREATH = LOUNGE.breath.gain;      // +8 %

// --- the sun -------------------------------------------------------------------
const SUN_WIDTH = 0.35;           // of the frame's width, diameter
const SUN_ABOVE_COVER = 0.25;     // of the cover's half-size
const STRIPE_BEATS = 4;
const STRIPE_ZONE = 0.5;          // the lower half of the visible disc
const STRIPES = 5;
const COVER_MIX = 0.20;
const COVER_CONTRAST = 0.90;

const SAFE_FLOOR = 0.6;
const FREE_BPM = 110;
const DROP_SPEED = 1.5;
const DROP_BARS = 8;
const EQ_SMOOTH_S = 0.4;
const CLOUD_PERIOD = 40;         // x units of a baked cloud profile (it repeats)
const LUM_W = 128, LUM_H = 72;
const LUM_EVERY = 12;

// Four skies (linear RGB). The cover's is built from its palette.
const SKIES = {
  sunset: {
    top: [0.020, 0.003, 0.055], horizon: [0.50, 0.030, 0.30], fill: [0.010, 0.002, 0.022],
    grid: [1.00, 0.10, 0.62], foot: [1.00, 0.06, 0.55], peak: [0.05, 0.80, 1.00],
    sunTop: [1.00, 0.80, 0.22], sunBottom: [1.00, 0.06, 0.42]
  },
  night: {
    top: [0.002, 0.004, 0.030], horizon: [0.035, 0.10, 0.42], fill: [0.002, 0.004, 0.020],
    grid: [0.10, 0.42, 1.00], foot: [0.40, 0.10, 1.00], peak: [0.10, 0.95, 1.00],
    sunTop: [0.80, 0.88, 1.00], sunBottom: [0.32, 0.20, 1.00]
  },
  dawn: {
    top: [0.030, 0.008, 0.050], horizon: [0.75, 0.20, 0.045], fill: [0.016, 0.004, 0.012],
    grid: [1.00, 0.30, 0.08], foot: [1.00, 0.10, 0.30], peak: [1.00, 0.72, 0.18],
    sunTop: [1.00, 0.90, 0.50], sunBottom: [1.00, 0.25, 0.06]
  }
};
const SKY_ORDER = ['sunset', 'night', 'dawn', 'cover'];
const SKY_KEYS = ['top', 'horizon', 'fill', 'grid', 'foot', 'peak', 'sunTop', 'sunBottom'];

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
}
function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function smooth01(x) { const t = clamp01(x); return t * t * (3 - 2 * t); }

// ---------------------------------------------------------------------------
// Shaders
// ---------------------------------------------------------------------------

const SAFE_FRAGMENT = `
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float safeAtt(vec2 ndc) {
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;

const planeVertexShader = `
  precision highp float;
  uniform mat4 uProj;
  uniform mat4 uView;
  uniform float uOff;        // the mesh's advance within one row
  uniform float uSnap;       // grid coordinate of the row at local z 0 (a multiple of the row)
  uniform float uMtnH;
  uniform float uEq;
  uniform vec2 uEqLR;        // the near ridges' gain, left / right
  varying vec2 vGrid;        // grid coordinate: x, z along the scroll
  varying float vH;          // height / uMtnH
  varying float vDepth;      // distance ahead of the camera
  varying vec2 vNdc;

  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  // Value noise whose z lattice wraps every n cells: the scroll's wrap
  // lands on the same ridges.
  float vnoise(vec2 p, float n) {
    vec2 i = floor(p), f = fract(p);
    vec2 u = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);
    float z0 = mod(i.y, n), z1 = mod(i.y + 1.0, n);
    float a = hash(vec2(i.x, z0)), b = hash(vec2(i.x + 1.0, z0));
    float c = hash(vec2(i.x, z1)), d = hash(vec2(i.x + 1.0, z1));
    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
  }

  void main() {
    float x = position.x;
    float gz = position.z - uSnap;
    float zw = position.z + uOff;
    float ax = abs(x);
    float mask = smoothstep(4.0, 12.0, ax) * (1.0 - smoothstep(30.0, 38.0, ax));
    float far = 1.0 - smoothstep(${(0.70 * DEPTH).toFixed(1)}, ${(0.96 * DEPTH).toFixed(1)}, -zw);
    float n = 0.0;
    if (mask * far > 0.0) {
      n = vnoise(vec2(x, gz) / 8.0, ${(SCROLL_PERIOD / 8).toFixed(1)}) * 0.68
        + vnoise(vec2(x + 31.0, gz) / 4.0, ${(SCROLL_PERIOD / 4).toFixed(1)}) * 0.32;
      n = pow(clamp(n * 1.35 - 0.22, 0.0, 1.0), 1.35);
    }
    // EQ: the nearest ridges follow the bands, low on the left, high on the right.
    float nearRidge = smoothstep(4.0, 7.0, ax) * (1.0 - smoothstep(13.0, 17.0, ax))
                    * (1.0 - smoothstep(16.0, 28.0, -zw));
    float eq = mix(1.0, x < 0.0 ? uEqLR.x : uEqLR.y, uEq * nearRidge);
    float h = n * mask * far * eq;
    vec3 p = vec3(x, h * uMtnH, zw);
    vH = h;
    vGrid = vec2(x, gz);
    vDepth = -zw;
    gl_Position = uProj * uView * vec4(p, 1.0);
    vNdc = gl_Position.xy / gl_Position.w;
  }
`;

const planeFragmentShader = `
  precision highp float;
  uniform vec3 uGridCol;
  uniform vec3 uFootCol;
  uniform vec3 uPeakCol;
  uniform vec3 uFillCol;
  uniform vec3 uFogCol;
  uniform vec3 uSunStreak;
  uniform vec2 uSun;          // centre, height units
  uniform float uSunR;
  uniform float uHorizon;
  uniform float uLineGain;
  uniform float uBarZ;        // the bar's travelling line, depth ahead (0: none)
  uniform float uBarA;
  uniform float uGain;
  uniform float uDebug;
  ${SAFE_FRAGMENT}
  varying vec2 vGrid;
  varying float vH;
  varying float vDepth;
  varying vec2 vNdc;

  // Lines at integer g: x[0] intensity of the core, x[1] of the halo.
  // Width in pixels from the line's world width at this depth, never under
  // one pixel (it dims instead), and fading into its mean coverage once the
  // cells get down to a few pixels.
  vec2 gridLine(float g, float fw, float wWorld, float coreMax) {
    fw = max(fw, 1e-5);
    float dpx = abs(fract(g + 0.5) - 0.5) / fw;
    float wpx = wWorld / fw;
    float corePx = clamp(wpx, 0.5, coreMax);
    float sub = clamp(wpx / 0.5, 0.0, 1.0);
    float core = (1.0 - smoothstep(corePx - 0.5, corePx + 0.6, dpx)) * sub;
    float halo = exp(-dpx / ${HALO_PX.toFixed(1)}) * ${HALO_GAIN.toFixed(2)} * sub;
    float dense = smoothstep(0.12, 0.45, fw);
    float meanCore = min(1.0, 2.0 * wWorld);
    float meanHalo = min(1.0, 2.0 * ${HALO_PX.toFixed(1)} * fw) * ${HALO_GAIN.toFixed(2)} * sub;
    return vec2(mix(core, meanCore, dense), mix(halo, meanHalo, dense));
  }

  void main() {
    float gx = vGrid.x + 0.5;            // lines at half cases: the road's edges at +-1.5
    float gz = vGrid.y;
    vec2 fw = fwidth(vec2(gx, gz));
    vec2 lx = gridLine(gx, fw.x, ${LINE_W.toFixed(3)}, ${CORE_MAX.toFixed(2)});
    vec2 lz = gridLine(gz, fw.y, ${LINE_W.toFixed(3)}, ${CORE_MAX.toFixed(2)});
    float ax = abs(vGrid.x);
    float road = 1.0 - smoothstep(${ROAD_HALF.toFixed(2)} - 0.02, ${ROAD_HALF.toFixed(2)} + 0.02, ax);
    // Inside the road, the grid is dim; its two edges are the brightest lines.
    float edge = 1.0 - smoothstep(0.0, 0.3, abs(ax - ${ROAD_HALF.toFixed(2)}));
    float inner = road * (1.0 - edge);
    vec2 lxr = lx * mix(1.0, 0.30, inner) * (1.0 + 1.2 * edge);
    vec2 lzr = lz * mix(1.0, 0.30, road);
    float core = max(lxr.x, lzr.x);
    if (uDebug > 0.5) { gl_FragColor = vec4(vec3(max(lx.x, lz.x)), 1.0); return; }   // ?outrunlines=1: the bare cores
    float halo = lxr.y + lzr.y;
    // The dashes: white, one case long every two, scrolling with the grid.
    float dxp = ax / max(fw.x, 1e-5);
    float dwpx = clamp(0.06 / max(fw.x, 1e-5), 0.5, 3.0);
    float dashX = (1.0 - smoothstep(dwpx - 0.5, dwpx + 0.6, dxp)) * clamp(0.06 / max(fw.x, 1e-5) / 0.5, 0.0, 1.0);
    float dz = fract(gz * 0.5);
    float aaz = max(fw.y * 0.5, 1e-4);
    float dashZ = smoothstep(0.0, aaz, dz) * (1.0 - smoothstep(0.5 - aaz, 0.5, dz));
    dashZ = mix(dashZ, 0.5, smoothstep(0.1, 0.4, fw.y));
    float dash = dashX * dashZ;
    // Colour: the floor's grid colour, the mountains from foot to peak.
    float hm = smoothstep(0.02, 0.85, vH);
    vec3 lineCol = mix(uGridCol, mix(uFootCol, uPeakCol, hm), smoothstep(0.0, 0.08, vH));
    vec3 white = vec3(1.0, 0.95, 0.98);
    vec3 col = uFillCol * (1.0 - 0.8 * road);
    col += lineCol * (core * 0.62 + halo * 0.85) * uLineGain;
    col += mix(lineCol, white, 0.55) * core * 0.45 * uLineGain * (1.0 + 1.5 * edge * road);
    col += white * dash * 1.15;
    // The bar's line, from the horizon to the camera in one bar.
    if (uBarA > 0.0) {
      float fd = max(fwidth(vDepth), 1e-4);
      float bpx = abs(vDepth - uBarZ) / fd;
      float bl = (1.0 - smoothstep(0.6, 1.8, bpx)) + exp(-bpx / 4.0) * 0.35;
      col += mix(lineCol, white, 0.4) * bl * uBarA;
    }
    // The sun on the road: a vertical streak of its colour, carried by the lines.
    vec2 s = vec2(vNdc.x * uAspect, vNdc.y);
    float sx = (s.x - uSun.x) / (0.42 * uSunR);
    float below = max(uHorizon - s.y, 0.0);
    float streak = exp(-sx * sx) * exp(-below / 0.55) * (1.0 - 0.6 * smoothstep(0.0, 0.3, vH));
    col += uSunStreak * streak * (0.05 + 0.9 * (core + halo));
    // Fog toward the horizon's colour.
    float fog = smoothstep(${(0.18 * DEPTH).toFixed(1)}, ${(0.93 * DEPTH).toFixed(1)}, vDepth);
    col = mix(col, uFogCol, fog);
    col *= uGain * safeAtt(vNdc);
    gl_FragColor = vec4(col, 1.0);
  }
`;

const skyVertexShader = `
  varying vec2 vNdc;
  void main() { vNdc = position.xy; gl_Position = vec4(position.xy, 0.9999, 1.0); }
`;

const skyFragmentShader = `
  precision highp float;
  uniform vec3 uTopCol;
  uniform vec3 uHorizonCol;
  uniform vec3 uFogCol;
  uniform vec3 uSunTop;
  uniform vec3 uSunBottom;
  uniform vec2 uSun;
  uniform float uSunR;
  uniform float uHorizon;
  uniform float uStripe;       // stripes passed, continuous (beats / 4)
  uniform float uPxUnit;       // pixels per height unit
  uniform float uStarCell;     // px
  uniform float uTime;
  uniform float uClouds;
  uniform float uCloudX;
  uniform sampler2D uCloudTex;
  uniform sampler2D uCover;
  uniform float uHasCover;
  uniform vec2 uCoverC;
  uniform float uCoverHalf;
  uniform float uGain;
  ${SAFE_FRAGMENT}
  varying vec2 vNdc;

  float hash1(vec2 p) { return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }
  vec4 hash4(vec2 p) {
    return fract(sin(vec4(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)),
                          dot(p, vec2(419.2, 371.9)), dot(p, vec2(23.7, 97.1)))) * 43758.5453);
  }

  void main() {
    vec2 p = vec2(vNdc.x * uAspect, vNdc.y);
    float aa = 1.0 / uPxUnit;
    float up = p.y - uHorizon;
    // The sky: deep violet at the top to the horizon's colour.
    float t = clamp(up / max(1.0 - uHorizon, 0.2), 0.0, 1.0);
    vec3 col = mix(uHorizonCol, uTopCol, pow(t, 0.62));
    if (up < 0.0) col = uFogCol;
    // A glowing horizon line.
    col += uHorizonCol * (exp(-abs(up) * uPxUnit / 2.0) * 0.55 + exp(-abs(up) / 0.05) * 0.18);
    // Stars: one per cell at most, fixed, twinkling slowly.
    vec2 pp = p * uPxUnit;
    vec2 cell = floor(pp / uStarCell);
    vec4 h = up > 0.04 ? hash4(cell) : vec4(1.0);
    if (h.x < 0.72) {
      vec2 sp = (cell + 0.15 + 0.7 * h.yz) * uStarCell;
      float r = 0.55 + 0.8 * h.w;
      float d = length(pp - sp);
      float tw = 0.55 + 0.45 * sin(uTime * (0.35 + 0.9 * h.y) + 6.28 * h.z);
      float star = (1.0 - smoothstep(r - 0.3, r + 0.7, d)) * tw * (0.35 + 0.5 * h.w);
      col += vec3(0.85, 0.88, 1.0) * star * smoothstep(0.04, 0.30, up);
    }
    // The sun.
    vec2 q = p - uSun;
    float d = length(q);
    float v = up / uSunR;                      // 0 at the horizon, 1 at the top
    float disc = 0.0;
    vec3 sun = vec3(0.0);
    // Only the disc's own pixels pay for the gradient, the cover and the stripes.
    if (d < uSunR + 2.0 * aa && up > -aa) {
    sun = mix(uSunBottom * 0.80, uSunTop * 1.04, smoothstep(0.0, 1.0, v));   // the top: luminance 0.84, just over the bloom
    // The cover, in grey, low contrast, blurred, at 20 %.
    vec2 cuv = (p - uCoverC) / (2.0 * uCoverHalf) + 0.5;
    float L = dot(textureLod(uCover, cuv, 2.5).rgb, vec3(0.2126, 0.7152, 0.0722));
    L = 0.5 + (L - 0.5) * ${COVER_CONTRAST.toFixed(2)};
    sun *= mix(1.0, mix(1.0, 2.0 * L, ${COVER_MIX.toFixed(2)}), uHasCover);
    disc = 1.0 - smoothstep(uSunR - aa, uSunR + aa, d);
    // The stripes: cut in the lower half of the visible disc, thicker as
    // they sink, one per STRIPE_BEATS beats.
    float zs = v / ${STRIPE_ZONE.toFixed(2)};
    if (zs < 1.0) {
      float w = zs * ${STRIPES.toFixed(1)} + uStripe;
      float g = mix(0.62, 0.0, zs);
      float dw = 0.75 * ${STRIPES.toFixed(1)} / (${STRIPE_ZONE.toFixed(2)} * uSunR * uPxUnit);   // fwidth(w), analytically (inside a branch)
      float gap = 1.0 - smoothstep(0.5 * g - dw, 0.5 * g + dw, abs(fract(w) - 0.5));
      gap *= smoothstep(0.0, 2.0 * dw, g);
      disc *= 1.0 - gap;
    }
    disc *= smoothstep(-aa, aa, up);
    }
    // The halo, wide and under the bloom threshold.
    float halo = exp(-max(d - uSunR, 0.0) / (0.32 * uSunR)) * 0.30 * smoothstep(-0.02, 0.04, up);
    col += mix(uSunBottom, uSunTop, 0.35) * halo * (1.0 - disc);
    // Clouds: three flat bands, dark against the sun, drifting.
    // only the band of rows the three clouds can reach pays for them
    if (uClouds > 0.0 && up > 0.2 * uSunR && up < 0.85 * uSunR) {
      for (int k = 0; k < 3; k++) {
        float fk = float(k);
        float yc = uHorizon + uSunR * (0.30 + 0.24 * fk);
        float th = uSunR * (0.050 + 0.018 * mod(fk * 2.0, 3.0));
        float x = p.x * (1.6 - 0.25 * fk) + uCloudX * (1.0 - 0.3 * fk) + 13.0 * fk;
        // the cloud's profile, baked on the CPU (27 sines a pixel cost the Pi 7-10 ms)
        vec3 pr = texture2D(uCloudTex, vec2(x / ${CLOUD_PERIOD.toFixed(1)}, (fk + 0.5) / 3.0)).rgb;
        float top = yc + th * (0.25 + 0.75 * pr.r);
        float bot = yc - th * (0.30 + 0.45 * pr.g);
        float m = smoothstep(bot - aa, bot + aa, p.y) * (1.0 - smoothstep(top - aa, top + aa, p.y));
        m *= pr.b;
        vec3 cc = uTopCol * 1.4 + uHorizonCol * 0.06;
        disc *= 1.0 - m * uClouds;
        col = mix(col, cc, m * uClouds * 0.88);
      }
    }
    col = mix(col, sun, disc);
    col *= uGain * safeAtt(vNdc);
    gl_FragColor = vec4(col, 1.0);
  }
`;

// The three clouds' profiles along x, periodic over CLOUD_PERIOD: R the top
// edge, G the bottom edge, B the gaps between clouds. 512 x 3, built once.
let CLOUD_TEX = null;
function cloudTexture() {
  if (CLOUD_TEX) return CLOUD_TEX;
  const W = 512, data = new Uint8Array(W * 3 * 4);
  const w = (u, f, a) => 0.5 + 0.26 * Math.sin(2 * Math.PI * f[0] * u + a) + 0.15 * Math.sin(2 * Math.PI * f[1] * u + 2.1 * a) + 0.09 * Math.sin(2 * Math.PI * f[2] * u + 0.7 * a);
  const ss = (e0, e1, x) => { const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0))); return t * t * (3 - 2 * t); };
  for (let k = 0; k < 3; k++) {
    for (let i = 0; i < W; i++) {
      const u = i / W, o = (k * W + i) * 4;
      data[o] = Math.round(255 * Math.max(0, Math.min(1, w(u, [11, 21, 39], k))));
      data[o + 1] = Math.round(255 * Math.max(0, Math.min(1, w(u, [14, 27, 47], k + 1))));
      data[o + 2] = Math.round(255 * ss(0.38, 0.52, w(u, [3, 5, 9], k + 2)));
      data[o + 3] = 255;
    }
  }
  const t = new THREE.DataTexture(data, W, 3, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.wrapS = THREE.RepeatWrapping; t.wrapT = THREE.ClampToEdgeWrapping;
  t.minFilter = t.magFilter = THREE.LinearFilter; t.generateMipmaps = false; t.needsUpdate = true;
  CLOUD_TEX = t;
  return t;
}

// Compiled once per tier and never released (pi-shader-compile-cost); the
// uniform objects live with the material (three-caches-the-uniform-object).
const MATERIALS = new Map();

function materialsFor() {
  if (MATERIALS.has('m')) return MATERIALS.get('m');
  const c3 = () => ({ value: new THREE.Color() });
  const shared = {
    uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
    uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
  };
  const sunU = { uSun: { value: new THREE.Vector2(0, 0.1) }, uSunR: { value: 0.6 }, uHorizon: { value: 0.1 }, uGain: { value: 1 } };
  const pu = Object.assign({
    uProj: { value: new THREE.Matrix4() }, uView: { value: new THREE.Matrix4() },
    uOff: { value: 0 }, uSnap: { value: 0 }, uMtnH: { value: MTN_H }, uEq: { value: 0 },
    uEqLR: { value: new THREE.Vector2(1, 1) },
    uGridCol: c3(), uFootCol: c3(), uPeakCol: c3(), uFillCol: c3(), uFogCol: c3(), uSunStreak: c3(),
    uLineGain: { value: 1 }, uBarZ: { value: 0 }, uBarA: { value: 0 }, uDebug: { value: 0 }
  }, sunU, shared);
  const su = Object.assign({
    uTopCol: c3(), uHorizonCol: c3(), uFogCol: pu.uFogCol, uSunTop: c3(), uSunBottom: c3(),
    uStripe: { value: 0 }, uPxUnit: { value: 360 }, uStarCell: { value: 40 }, uTime: { value: 0 },
    uClouds: { value: 0 }, uCloudX: { value: 0 }, uCloudTex: { value: cloudTexture() },
    uCover: { value: null }, uHasCover: { value: 0 }, uCoverC: { value: new THREE.Vector2() }, uCoverHalf: { value: 0.4 }
  }, sunU, shared);
  const plane = new THREE.ShaderMaterial({ uniforms: pu, vertexShader: planeVertexShader, fragmentShader: planeFragmentShader });
  const sky = new THREE.ShaderMaterial({
    uniforms: su, vertexShader: skyVertexShader, fragmentShader: skyFragmentShader,
    depthTest: true, depthWrite: false
  });
  const m = { plane, sky, pu, su };
  MATERIALS.set('m', m);
  return m;
}

// The plane: nx columns (uniform out to X_CORE, then geometric to X_MAX,
// so the far edge still reaches the frame's sides), nz rows of DEPTH / nz.
function buildPlane(nx, nz) {
  const half = nx / 2;
  const core = Math.round(half * 0.72);
  const xs = [];
  const dxc = X_CORE / core;
  const r = Math.pow(X_MAX / X_CORE, 1 / (half - core));
  for (let k = -half; k <= half; k++) {
    const a = Math.abs(k);
    const x = a <= core ? a * dxc : X_CORE * Math.pow(r, a - core);
    xs.push(Math.sign(k) * x);
  }
  const dz = DEPTH / nz;
  const pos = new Float32Array((nx + 1) * (nz + 1) * 3);
  let o = 0;
  for (let j = 0; j <= nz; j++) {
    for (let i = 0; i <= nx; i++) { pos[o++] = xs[i]; pos[o++] = 0; pos[o++] = -j * dz; }
  }
  const idx = new Uint16Array(nx * nz * 6);
  o = 0;
  for (let j = 0; j < nz; j++) {
    for (let i = 0; i < nx; i++) {
      const a = j * (nx + 1) + i, b = a + 1, c = a + nx + 1, d = c + 1;
      // counter-clockwise seen from above: the floor faces up
      idx[o++] = a; idx[o++] = b; idx[o++] = c; idx[o++] = b; idx[o++] = d; idx[o++] = c;
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setIndex(new THREE.BufferAttribute(idx, 1));
  return { geometry: g, dz };
}

const _size = new THREE.Vector2();
const _hsl = { h: 0, s: 0, l: 0 };

export class OutrunVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];
    const sky = urlParam('outrunsky');
    this._forcedSky = SKY_ORDER.indexOf(sky) >= 0 ? sky : null;
    this._forcedEq = urlParam('outruneq') === '1';
    this._debugDrop = urlParam('outrundrop') === '1';
    this._forcedClouds = urlParam('outrunclouds');
    this._debugLines = urlParam('outrunlines') === '1';
    this._post = new VdjPost({ bloomStrength: this._debugLines ? 0 : 0.55, bloomThreshold: 0.78 });
    this._comfort = new VdjComfort(LOUNGE.comfort);
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene;
    const m = materialsFor();
    this._m = m;
    this._pu = m.pu; this._su = m.su;
    const built = buildPlane(this._layout.nx, this._layout.nz);
    this._dz = built.dz;
    this._plane = new THREE.Mesh(built.geometry, m.plane);
    this._plane.frustumCulled = false;
    this._plane.renderOrder = 0;
    this._skyQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), m.sky);
    this._skyQuad.frustumCulled = false;
    this._skyQuad.renderOrder = 10;
    const self = this;
    this._plane.onBeforeRender = function (renderer) { self._beforeRender(renderer); };
    scene.add(this._plane);
    scene.add(this._skyQuad);

    this._cam = new THREE.PerspectiveCamera(FOV, 16 / 9, 0.05, 600);
    this._ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    // The comfort measurement: the same two meshes, rendered small.
    this._lumTarget = new THREE.WebGLRenderTarget(LUM_W, LUM_H, {
      minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType, depthBuffer: true, stencilBuffer: false, generateMipmaps: false
    });
    this._lumScene = new THREE.Scene();
    const lp = new THREE.Mesh(built.geometry, m.plane); lp.frustumCulled = false; lp.renderOrder = 0;
    const ls = new THREE.Mesh(this._skyQuad.geometry, m.sky); ls.frustumCulled = false; ls.renderOrder = 10;
    this._lumScene.add(lp); this._lumScene.add(ls);
    this._lumPixels = new Uint8Array(LUM_W * LUM_H * 4);

    // --- state ---
    this._time = 0;
    this._paused = false;
    this._beatSeconds = 60 / FREE_BPM;
    this._lastBeatPhase = null; this._lastBarPhase = null; this._lastPhrase = null; this._lastMode = null;
    this._beatCount = 0; this._barCount = 0; this._phraseCount = 0; this._freeBeat = 0;
    this._sinceBeat = 10; this._sinceBar = 10; this._beatPhase = 0; this._barPhase = 0; this._beats = 0;
    this._scroll = 0;
    this._speed = 1; this._speedTarget = 1;
    this._stripe = 0;
    this._low = 0.5; this._mid = 0.5; this._high = 0.5;
    this._eqL = 1; this._eqR = 1;
    this._eq = this._forcedEq ? 1 : 0; this._eqTarget = this._eq;
    this._clouds = 0; this._cloudsTarget = 0; this._cloudX = 0;
    this._cloudPhrase = false;
    this._intensity = 1;
    this._dropUntil = -1; this._dropAt = -1e9; this._lastDropTime = -1e9; this._debugDropAt = 4;
    this._frame = 0; this._lastLumAt = 0; this._comfortGain = 1;
    this._camX = 0;
    this._hexes = null;
    this._lastPaletteRef = undefined;
    this._lastArtworkUrl = undefined;
    this._artworkTexture = null;
    this._su.uCover.value = null; this._su.uHasCover.value = 0;
    this._pu.uDebug.value = this._debugLines ? 1 : 0;
    this._pu.uGain.value = 1;
    // Skies: the current one, and the fade's start / end.
    this._skyIndex = this._forcedSky ? SKY_ORDER.indexOf(this._forcedSky) : 0;
    this._skyCover = this._coverSky(null);
    this._skyFrom = this._skyAt(this._skyIndex);
    this._skyTo = this._skyFrom;
    this._skyFade = 1; this._skyFadeBeats = 4;
    this._skyNow = {};
    for (const k of SKY_KEYS) this._skyNow[k] = new THREE.Color();
    this._applySky(1);
    this._layoutSun();
    this._writeUniforms();
  }

  // --- skies ---------------------------------------------------------------------

  _coverSky(hexes) {
    if (!hexes || hexes.length < 2) return SKIES.sunset;
    const c = (i, sat, light) => {
      const o = vdjColor(hexes[i % hexes.length], new THREE.Color(), LOUNGE.saturation);
      if (light != null) { o.getHSL(_hsl); o.setHSL(_hsl.h, _hsl.s, light); }
      return [o.r, o.g, o.b];
    };
    const sunTop = new THREE.Color().fromArray(c(2, 0.85, 0.62)).lerp(new THREE.Color(1, 0.9, 0.6), 0.35);
    return {
      top: c(1, 0.85, 0.035), horizon: c(0, 0.85, 0.26), fill: c(1, 0.85, 0.012),
      grid: c(0), foot: c(0), peak: c(1),
      sunTop: [sunTop.r, sunTop.g, sunTop.b], sunBottom: c(3 % hexes.length)
    };
  }

  _skyAt(i) {
    const name = SKY_ORDER[i];
    return name === 'cover' ? this._skyCover : SKIES[name];
  }

  _applySky(k) {
    const a = this._skyFrom, b = this._skyTo, n = this._skyNow;
    const u = smooth01(k);
    for (const key of SKY_KEYS) {
      const x = a[key], y = b[key];
      n[key].setRGB(x[0] + (y[0] - x[0]) * u, x[1] + (y[1] - x[1]) * u, x[2] + (y[2] - x[2]) * u);
    }
    const pu = this._pu, su = this._su;
    pu.uGridCol.value.copy(n.grid); pu.uFootCol.value.copy(n.foot); pu.uPeakCol.value.copy(n.peak);
    pu.uFillCol.value.copy(n.fill);
    pu.uFogCol.value.copy(n.horizon).multiplyScalar(0.92);
    su.uTopCol.value.copy(n.top); su.uHorizonCol.value.copy(n.horizon);
    su.uSunTop.value.copy(n.sunTop); su.uSunBottom.value.copy(n.sunBottom);
    pu.uSunStreak.value.copy(n.sunBottom).lerp(n.sunTop, 0.4).multiplyScalar(0.55);
  }

  // A new sky, cut or faded over one bar.
  _changeSky(fade) {
    if (this._forcedSky) return;
    let next = (this._skyIndex + 1) % SKY_ORDER.length;
    if (SKY_ORDER[next] === 'cover' && !this._hexes) next = (next + 1) % SKY_ORDER.length;
    this._skyFrom = {};
    for (const k of SKY_KEYS) this._skyFrom[k] = this._skyNow[k].toArray();
    this._skyIndex = next;
    this._skyTo = this._skyAt(next);
    this._skyFade = fade ? 0 : 1;
    this._skyFadeBeats = 4;
    if (!fade) this._applySky(1);
  }

  // --- the sun's place: from the cover's --------------------------------------------

  _layoutSun() {
    const aspect = safeZoneUniforms.uAspect.value || 16 / 9;
    // R19: the sun and the horizon sit on the centre of composition (the
    // cover block as it would sit centred), not on the cover's position.
    const c = safeZoneUniforms.uStageCenter.value, hs = safeZoneUniforms.uStageHalfSize.value;
    // The overlay holds the cover and, under it, the title: a block taller
    // than wide has its square cover at the top.
    const coverHalf = Math.min(hs.x, hs.y);
    const coverCy = hs.y > hs.x * 1.12 ? c.y + hs.y - hs.x : c.y;
    let yh = coverCy + SUN_ABOVE_COVER * coverHalf;
    yh = Math.max(-0.2, Math.min(0.4, yh));
    let R = SUN_WIDTH * aspect;
    R = Math.min(R, 0.97 - yh);
    R = Math.max(R, Math.min(0.97 - yh, coverHalf * 1.15));
    this._horizon = yh; this._sunR = R;
    const su = this._su;
    su.uSun.value.set(c.x, yh);
    su.uSunR.value = R;
    su.uHorizon.value = yh;
    // The cover in the sun: a square of the disc's diameter on the sun's
    // centre - its upper half is the visible disc, mostly outside the DOM cover.
    su.uCoverC.value.set(c.x, yh);
    su.uCoverHalf.value = R;
  }

  _loadArtwork(url) {
    const self = this;
    loadArtworkTexture(url, { wrap: THREE.ClampToEdgeWrapping, mipmaps: true }, function (texture) {
      if (!self._scene) { texture.dispose(); return; }
      if (self._artworkTexture) self._artworkTexture.dispose();
      self._artworkTexture = texture;
      self._su.uCover.value = texture;
      self._su.uHasCover.value = 1;
    }, function () { /* no cover in the sun, then */ });
  }

  _applyPalette(palette) {
    const vivid = palette && palette.vividColors && palette.vividColors.length >= 2 ? palette.vividColors : null;
    this._hexes = vivid ? vivid.slice(0, 4) : null;
    this._skyCover = this._coverSky(this._hexes);
    if (SKY_ORDER[this._skyIndex] === 'cover') { this._skyTo = this._skyCover; if (this._skyFade >= 1) this._applySky(1); }
  }

  onTrackChange(profile) { this._applyPalette(profile && profile.artworkPalette); }

  // --- the frame -------------------------------------------------------------------

  update(deltaTime, profile) {
    const director = profile.director || {};
    if (profile.artworkPalette !== this._lastPaletteRef) { this._lastPaletteRef = profile.artworkPalette; this._applyPalette(profile.artworkPalette); }
    if (profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      if (profile.artworkUrl) this._loadArtwork(profile.artworkUrl);
      else { this._su.uHasCover.value = 0; this._su.uCover.value = null; }
    }
    const dt = Math.min(deltaTime, 0.05);
    this._dtFrame = dt;
    this._time += dt;
    this._paused = profile.playbackState !== 'PLAYING';
    const bpmRaw = profile.trackFeatures && profile.trackFeatures.bpm;
    const bpm = bpmRaw > 0 ? THREE.MathUtils.clamp(bpmRaw, 50, 200) : FREE_BPM;
    this._beatSeconds = 60 / bpm;
    const beat = this._beatSeconds;
    const measured = !!director.hasReliableGrid;
    const bands = measured ? director.bands : null;

    // --- the clocks: a continuous beat count, advanced by the grid's phase ---
    let beatEdge = false, barEdge = false, phraseEdge = false;
    let dBeats = 0;
    if (this._paused) {
      dBeats = 0;
    } else if (profile.beatPhase != null) {
      if (this._lastBeatPhase != null) {
        let d = profile.beatPhase - this._lastBeatPhase;
        if (d < -0.5) { d += 1; beatEdge = true; }
        // A seek or a re-anchor: never more than twice the nominal rate, never backwards.
        dBeats = Math.max(0, Math.min(d, 2 * dt / beat));
      }
      this._lastBeatPhase = profile.beatPhase;
      this._beatPhase = profile.beatPhase;
    } else {
      dBeats = dt / beat;
      this._freeBeat += dBeats;
      if (this._freeBeat >= 1) { this._freeBeat -= 1; beatEdge = true; }
      this._beatPhase = this._freeBeat;
    }
    this._beats += dBeats;
    this._sinceBeat += dt; this._sinceBar += dt;
    if (beatEdge) { this._beatCount++; this._sinceBeat = 0; }
    if (profile.barPhase != null && !this._paused) {
      if (this._lastBarPhase != null && profile.barPhase < this._lastBarPhase - 0.5) barEdge = true;
      this._lastBarPhase = profile.barPhase;
      this._barPhase = profile.barPhase;
    } else if (!this._paused) {
      if (beatEdge && this._beatCount % 4 === 0) barEdge = true;
      this._barPhase = ((this._beatCount % 4) + this._beatPhase) / 4;
    }
    if (barEdge) { this._barCount++; this._sinceBar = 0; }
    const phrase = profile.phraseIndex;
    if (phrase != null && this._lastPhrase != null && phrase !== this._lastPhrase) phraseEdge = true;
    if (phrase == null && barEdge && this._barCount % 4 === 0) phraseEdge = true;
    if (phrase != null) this._lastPhrase = phrase;

    // --- the bands, smoothed ---
    const kb = 1 - Math.exp(-dt / 0.25);
    const fl = 0.5 + 0.18 * Math.sin(this._time * 0.9), fm = 0.5 + 0.15 * Math.sin(this._time * 0.53 + 1), fh = 0.5 + 0.18 * Math.sin(this._time * 1.3 + 2);
    this._low += ((bands ? clamp01(bands.low || 0) : fl) - this._low) * kb;
    this._mid += ((bands ? clamp01(bands.mid || 0) : fm) - this._mid) * kb;
    this._high += ((bands ? clamp01(bands.high || 0) : fh) - this._high) * kb;
    // The EQ's two sides, over 400 ms. Without a measured analysis, a soft
    // kick on the left and the off-beat on the right stand in for the bands.
    const ke = 1 - Math.exp(-dt / EQ_SMOOTH_S);
    const pulseL = Math.exp(-this._sinceBeat / (0.45 * beat));
    const pulseR = Math.exp(-Math.abs(this._beatPhase - 0.5) * 4);
    const eqL = bands ? clamp01(bands.low || 0) : 0.25 + 0.6 * pulseL;
    const eqR = bands ? clamp01(bands.high || 0) : 0.25 + 0.5 * pulseR;
    this._eqL += (0.3 + 1.4 * eqL - this._eqL) * ke;
    this._eqR += (0.3 + 1.4 * eqR - this._eqR) * ke;

    // --- drop ---
    let dropNow = false;
    const ev = director.nextEvent;
    if (measured && director.mode === 'drop' && this._lastMode !== 'drop') dropNow = true;
    if (ev && ev.type === 'drop' && ev.msUntil != null && ev.msUntil <= 0) dropNow = true;
    this._lastMode = director.mode;
    if (this._debugDrop && this._time >= this._debugDropAt) { dropNow = true; this._debugDropAt = this._time + 12 * 4 * beat; }
    if (dropNow && this._time - this._lastDropTime > 8 * beat) {
      this._lastDropTime = this._time;
      this._dropAt = this._beats;
      this._dropUntil = this._beats + DROP_BARS * 4;
    }
    const inDrop = this._dropUntil > 0 && this._beats < this._dropUntil;
    if (this._dropUntil > 0 && !inDrop) this._dropUntil = -1;

    // --- phrases: one in two changes the sky; EQ on the even ones ---
    if (phraseEdge && !inDrop) {
      this._phraseCount++;
      if (this._phraseCount % 2 === 0) {
        this._changeSky(Math.random() < 0.6);
        this._cloudPhrase = Math.random() < 0.5;
      }
    }
    this._eqTarget = this._forcedEq || inDrop || (this._phraseCount % 2 === 0 && this._phraseCount > 0) ? 1 : 0;
    const cloudsOk = this._layout.clouds && this._forcedClouds !== '0';
    this._cloudsTarget = cloudsOk && (this._forcedClouds === '1' || inDrop || this._cloudPhrase) ? 1 : 0;
    this._speedTarget = inDrop ? DROP_SPEED : 1;
    // Every change is a one-bar ease: lounge, no jolts.
    const kBar = dBeats / 4;
    const toward = (x, target, k) => x + Math.max(-k, Math.min(k, target - x));
    this._eq = toward(this._eq, this._eqTarget, kBar);
    this._clouds = toward(this._clouds, this._cloudsTarget, kBar);
    this._speed = toward(this._speed, this._speedTarget, kBar * (DROP_SPEED - 1));
    const rise = inDrop ? 1 + LOUNGE.dropRise.gain * smooth01((this._beats - this._dropAt) / (4 * LOUNGE.dropRise.bars)) : 1;
    this._intensity = toward(this._intensity, rise, inDrop ? 1 : kBar * LOUNGE.dropRise.gain);
    if (this._skyFade < 1) { this._skyFade = Math.min(1, this._skyFade + dBeats / this._skyFadeBeats); this._applySky(this._skyFade); }

    // --- motion: one case per beat ---
    this._scroll = (this._scroll + dBeats * this._speed) % SCROLL_PERIOD;
    this._stripe += dBeats / STRIPE_BEATS;
    if (this._stripe > 1000) this._stripe -= 1000;
    this._cloudX += dt * (this._paused ? 0.002 : 0.012);
    this._camX = 0.35 * Math.sin(2 * Math.PI * vdjLoop(this._beats, 64));
    this._camY = CAM_H + 0.12 * Math.sin(2 * Math.PI * vdjLoop(this._beats, 32));

    this._writeUniforms();
    this._post.update(vdjBarChroma(this._sinceBar));
  }

  _writeUniforms() {
    const pu = this._pu, su = this._su;
    const dz = this._dz;
    const off = this._scroll % dz;
    pu.uOff.value = off;
    pu.uSnap.value = this._scroll - off;
    pu.uMtnH.value = MTN_H * (0.75 + 0.5 * this._mid);
    pu.uEq.value = this._eq;
    pu.uEqLR.value.set(this._eqL, this._eqR);
    const breath = vdjBeatBreath(this._sinceBeat, LOUNGE);
    pu.uLineGain.value = (0.8 + 0.4 * this._low) * breath * this._intensity;
    // The bar's line: linear in 1 / depth, so it slides down the frame at an
    // even pace from the horizon to the bottom edge.
    const bp = this._barPhase || 0;
    const inv = (1 / (0.9 * DEPTH)) * (1 - bp) + (1 / 3.0) * bp;
    pu.uBarZ.value = 1 / inv;
    pu.uBarA.value = 0.55 * smooth01(bp / 0.12) * this._intensity;
    su.uStripe.value = this._stripe;
    su.uTime.value = this._time;
    su.uClouds.value = this._clouds;
    su.uCloudX.value = this._cloudX;
  }

  _beforeRender(renderer) {
    renderer.getDrawingBufferSize(_size);
    const W = Math.max(1, _size.x), H = Math.max(1, _size.y);
    this._layoutSun();
    const su = this._su, pu = this._pu;
    su.uPxUnit.value = H / 2;
    // ~300 stars: the visible sky's area in cells of uStarCell px.
    const skyPx = W * H * Math.max(0.1, (1 - this._horizon) / 2);
    su.uStarCell.value = Math.sqrt(skyPx / (300 / 0.72));
    // The camera: the horizon at the sun's centre line.
    const cam = this._cam;
    cam.aspect = W / H;
    cam.fov = FOV;
    cam.updateProjectionMatrix();
    const pitch = Math.atan(this._horizon * Math.tan(FOV * Math.PI / 360));
    cam.position.set(this._camX || 0, this._camY || CAM_H, 0);
    cam.rotation.set(-pitch, 0, 0);
    cam.updateMatrixWorld(true);
    pu.uProj.value.copy(cam.projectionMatrix);
    pu.uView.value.copy(cam.matrixWorldInverse);
    // The governor moves its gain in steps of up to 30 %: applied as is, a
    // visible jump. Followed over ~1 s instead.
    const target = this._comfortGain != null ? this._comfortGain : 1;
    pu.uGain.value += (target - pu.uGain.value) * Math.min(1, (this._dtFrame || 1 / 60) / 1.0);

    this._frame++;
    if (this._frame % LUM_EVERY === 0) {
      const prev = renderer.getRenderTarget();
      renderer.setRenderTarget(this._lumTarget);
      renderer.clear();
      renderer.render(this._lumScene, this._ortho);
      renderer.readRenderTargetPixels(this._lumTarget, 0, 0, LUM_W, LUM_H, this._lumPixels);
      renderer.setRenderTarget(prev);
      let s = 0;
      const px = this._lumPixels;
      for (let i = 0; i < LUM_W * LUM_H; i++) {
        const l = (0.2126 * px[i * 4] + 0.7152 * px[i * 4 + 1] + 0.0722 * px[i * 4 + 2]) / 255;
        s += Math.pow(l, 1 / 2.2);
      }
      const dtS = this._time - this._lastLumAt;
      this._lastLumAt = this._time;
      this._comfortGain = this._comfort.measure(s / (LUM_W * LUM_H), Math.max(0.05, dtS));
    }
  }

  get probe() {
    return {
      t: this._time, beats: this._beats, scroll: this._scroll, speed: this._speed, stripe: this._stripe,
      sky: SKY_ORDER[this._skyIndex], skyFade: this._skyFade, eq: this._eq, eqLR: [this._eqL, this._eqR],
      clouds: this._clouds, drop: this._dropUntil > 0, intensity: this._intensity,
      horizon: this._horizon, sunR: this._sunR, sun: this._su.uSun.value.toArray(),
      gain: this._pu.uGain.value, meanLum: this._comfort.average, phrase: this._phraseCount,
      bar: this._barCount, barPhase: this._barPhase, dz: this._dz, hasCover: this._su.uHasCover.value,
      aberration: this._post.value.aberration
    };
  }

  dispose() {
    this._plane.onBeforeRender = null;
    this._scene.remove(this._plane);
    this._scene.remove(this._skyQuad);
    this._plane.geometry.dispose();
    this._skyQuad.geometry.dispose();
    this._lumTarget.dispose();
    if (this._artworkTexture) { this._artworkTexture.dispose(); this._artworkTexture = null; }
    this._su.uCover.value = null;
    this._scene = null;
  }
}
